import { Alert, Button, Group, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle } from "@tabler/icons-react";
import { useState } from "react";
import { useStainsOfDay, useConfirmPresence } from "../../hooks/useWorkflows";

export function MissingControlsBanner() {
  // Pass no date — backend uses business timezone to determine "today",
  // matching how the control slides page queries it.
  const { data: stainsOfDay } = useStainsOfDay();
  const confirmPresenceMutation = useConfirmPresence();
  const [confirming, setConfirming] = useState<Set<string>>(new Set());

  const missing =
    stainsOfDay?.filter((g) => g.presenceStatus === "pending") ?? [];

  if (missing.length === 0) return null;

  const handleConfirmOne = async (
    stainId: string,
    poolId: string | null,
    stainCode: string
  ) => {
    const key = `${poolId ?? "gcp"}-${stainId}`;
    setConfirming((prev) => new Set(prev).add(key));
    try {
      await confirmPresenceMutation.mutateAsync({
        stainId,
        poolId,
      });
      notifications.show({
        title: "Control Confirmed",
        message: `${stainCode} control slide received`,
        color: "green",
      });
    } catch {
      notifications.show({
        title: "Error",
        message: `Failed to confirm ${stainCode} control slide`,
        color: "red",
      });
    } finally {
      setConfirming((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  return (
    <Alert
      icon={<IconAlertCircle size={16} />}
      color="red"
      title={`${missing.length} stain${
        missing.length > 1 ? "s" : ""
      } missing control slide confirmation`}
      style={{ width: "75%", maxWidth: 650 }}
    >
      <Stack gap="md">
        {missing.map((g) => {
          const key = `${g.poolId ?? "gcp"}-${g.stainId}`;
          return (
            <Group
              key={key}
              justify="space-between"
              align="center"
              wrap="nowrap"
            >
              <Text size="sm">
                <Text size="sm" span fw={600}>
                  {g.stainCode}
                </Text>
                <Text size="sm" span c="dimmed">
                  {" "}
                  — {g.stainName} ({g.poolName})
                </Text>
              </Text>
              <Button
                size="xs"
                color="red"
                variant="filled"
                loading={confirming.has(key)}
                onClick={() =>
                  handleConfirmOne(g.stainId, g.poolId, g.stainCode)
                }
              >
                Slide Received
              </Button>
            </Group>
          );
        })}
      </Stack>
    </Alert>
  );
}
